import { useMutation } from '@apollo/client/react';
import { Button } from '@optools/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@optools/components/ui/dialog';
import { UPDATE_BROADCAST_OVERRIDE } from '@optools/services/operator-gateway/btc/mutations';
import { GET_BROADCAST_ACQUISITIONS } from '@optools/services/operator-gateway/btc/queries';
import { DecoderRegion, FeedTypeCode } from '@optools/services/operator-gateway/btc/types';
import { PipelineBroadcastOverride } from '@optools/services/operator-gateway/types/generated/graphql';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { BroadcastOverrideForm, BroadcastOverrideFormValues } from './broadcast-override-form';

export interface UpdateBroadcastOverrideDialogProps {
  feedTypeCode: FeedTypeCode;
  gamePk: number;
  override: PipelineBroadcastOverride;
}

export function UpdateBroadcastOverrideDialog({
  feedTypeCode,
  gamePk,
  override,
}: UpdateBroadcastOverrideDialogProps) {
  const [open, setOpen] = useState(false);
  const { callSign, fronthaulDecoderInputLabel, id, multicastAddress, region } = override;

  const [updateOverride, { loading }] = useMutation(UPDATE_BROADCAST_OVERRIDE, {
    onCompleted: ({ updateBroadcastOverride }) => {
      const title = 'Update Broadcast Override';

      if (!updateBroadcastOverride) {
        toast.error(title, {
          description: `Failed to update Broadcast Override ID: ${id}`,
          duration: Infinity,
        });
        return;
      }

      setOpen(false);
      toast.success(title, {
        description: `Updated Broadcast Override ID: ${updateBroadcastOverride.id}`,
      });
    },
    refetchQueries: [
      {
        query: GET_BROADCAST_ACQUISITIONS,
        variables: { feedTypeCode, gamePk },
      },
    ],
  });

  // Handle creating the broadcast override, if we are creating an override for the central
  // region ignore any incoming input label and use null until the central region supports an
  // input label
  const handleUpdateBroadcastOverride = (formValues: BroadcastOverrideFormValues) => {
    const { fronthaulDecoderInputLabel, multicastAddress } = formValues;
    updateOverride({
      variables: {
        id,
        region,
        updatedBroadcastOverride: {
          callSign,
          fronthaulDecoderInputLabel:
            region === DecoderRegion.EAST ? fronthaulDecoderInputLabel : null,
          multicastAddress,
        },
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => !loading && setOpen(newOpen)}>
      <DialogTrigger asChild>
        <Button aria-label="Update Broadcast Override" size="icon-xs">
          <Pencil />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Broadcast Override</DialogTitle>
          <DialogDescription className="sr-only">
            Update an existing broadcast override
          </DialogDescription>
        </DialogHeader>
        <BroadcastOverrideForm
          callSign={callSign}
          defaultValues={{
            fronthaulDecoderInputLabel: fronthaulDecoderInputLabel ?? undefined,
            multicastAddress: multicastAddress ?? undefined,
          }}
          disabled={loading}
          feedTypeCode={feedTypeCode}
          gamePk={gamePk}
          region={region as DecoderRegion}
          onCancel={() => setOpen(false)}
          onSubmit={handleUpdateBroadcastOverride}
        />
      </DialogContent>
    </Dialog>
  );
}
